import { updateDayOfBirth } from "@/lib/backend/profile-completeness";

/**
 * Normalise a date value (Date | ISO string | "" | null) to a `YYYY-MM-DD` string for comparison,
 * or null when empty. Used to detect whether the employee actually changed their day of birth
 * before triggering the verified save flow (#383).
 */
export function toDateInputValue(value: Date | string | null | undefined): string | null {
  if (value == null || value === "") {
    return null;
  }

  // Een string is al een kalenderdatum — uit `<input type="date">` als
  // "1990-01-01", uit de API als "1990-01-01T00:00:00.000Z". Die knippen we af in
  // plaats van hem door een `Date` te halen: `new Date("1990-01-01T00:00:00Z")`
  // is middernacht UTC, en `getDate()` maakte daar westelijk van Greenwich
  // 31 december 1989 van. Een geboortedatum die een dag verschuift meldt zich als
  // een wijziging, en dan vraagt het scherm bij elke opslag opnieuw om bevestiging.
  if (typeof value === "string") {
    const calendarDate = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
    if (calendarDate) {
      return calendarDate;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  // Een `Date` komt uit de datumkiezer en staat dus al op de klok van het
  // toestel; die velden zijn hier juist de bedoelde waarde.
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** True when the new value differs from the current one (and is non-empty). */
export function hasDayOfBirthChanged(
  current: Date | string | null | undefined,
  next: Date | string | null | undefined,
): boolean {
  const nextValue = toDateInputValue(next);
  if (nextValue == null) {
    return false;
  }
  return nextValue !== toDateInputValue(current);
}

/**
 * Persist a verified day-of-birth change. Always sends `confirmed: true` because the caller is
 * expected to have shown an explicit confirmation step first (geboortedatum drives loonschalen, so
 * it is never silently overwritten).
 */
export function submitVerifiedDayOfBirth(value: Date | string) {
  const normalized = toDateInputValue(value);
  if (normalized == null) {
    throw new Error("Invalid day of birth");
  }
  return updateDayOfBirth({ dayOfBirth: normalized, confirmed: true });
}
